import React, { useState } from 'react'

import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

interface ThumbsFeedbackProps {
  size?: 'small' | 'medium' | 'large'
  onClick?: (type: 'up' | 'down' | null) => void
  initialState?: 'up' | 'down' | null
}

const ThumbsFeedback = ({ size = 'medium', onClick, initialState = null }: ThumbsFeedbackProps) => {
  const [thumbsState, setThumbsState] = useState<'up' | 'down' | null>(initialState)
  const [isAnimating, setIsAnimating] = useState(false)
  const { toast } = useToast()

  const iconSizes = {
    small: 'w-3.5 h-3.5',
    medium: 'w-4 h-4',
    large: 'w-5 h-5',
  }

  const handleThumbClick = (type: 'up' | 'down') => {
    setIsAnimating(true)

    setTimeout(() => {
      setIsAnimating(false)
    }, 500)

    setThumbsState((prevState) => {
      const newState = prevState === type ? null : type

      if (newState === null) {
        toast({
          title: 'Feedback removed',
          description: 'Your feedback has been removed',
        })
      } else {
        toast({
          title: type === 'up' ? 'Positive feedback recorded' : 'Negative feedback recorded',
          description: type === 'up' ? '🙏 Thank you for your feedback!' : "🙏 We'll take your feedback into account",
        })
      }

      if (onClick) {
        onClick(newState)
      }

      return newState
    })
  }

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation()
          handleThumbClick('up')
        }}
        className={cn(
          'p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-transform',
          isAnimating && thumbsState === 'up' && 'animate-bounce'
        )}
      >
        <ThumbsUp
          className={iconSizes[size]}
          fill={thumbsState === 'up' ? '#62718D' : 'none'}
          color={thumbsState === 'up' ? '#62718D' : 'currentColor'}
        />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation()
          handleThumbClick('down')
        }}
        className={cn(
          'p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-transform',
          isAnimating && thumbsState === 'down' && 'animate-bounce-down'
        )}
      >
        <ThumbsDown
          className={iconSizes[size]}
          fill={thumbsState === 'down' ? '#62718D' : 'none'}
          color={thumbsState === 'down' ? '#62718D' : 'currentColor'}
        />
      </button>
    </div>
  )
}

export default ThumbsFeedback
